import { provideHttpClient, withInterceptorsFromDi, HttpClient, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  Provider,
  ErrorHandler,
  APP_INITIALIZER,
  Injector,
  importProvidersFrom,
  ApplicationConfig,
  provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, Router, RouteReuseStrategy, withComponentInputBinding, withPreloading } from '@angular/router';
import { provideIonicAngular, IonicRouteStrategy } from '@ionic/angular/standalone';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { register } from 'swiper/element/bundle';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { TranslationFactory, createTranslateLoader } from './app/core/factories/translate.factory';
import { appInterceptor } from './app/core/interceptors/app.interceptor';
import { authInterceptor } from './app/core/interceptors/auth.interceptor';
import { loadingInterceptor } from './app/core/interceptors/loading.interceptor';
import { StartupService } from './app/core/services/startup.service';
import { environment } from './environments/environment';

register();

const startupServiceFactory = (startupService: StartupService) => {
  return () => startupService.initializeApp();
};

const APP_INITIALIZERS: Provider[] = [
  StartupService,
  {
    provide: APP_INITIALIZER,
    useFactory: startupServiceFactory,
    deps: [StartupService],
    multi: true
  },
  {
    provide: APP_INITIALIZER,
    useFactory: TranslationFactory,
    deps: [TranslateService, Injector],
    multi: true
  }
];

const SENTRY: Provider[] = [
  { provide: ErrorHandler, useValue: SentryAngular.createErrorHandler() },
  { provide: SentryAngular.TraceService, deps: [Router] },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {},
    deps: [SentryAngular.TraceService],
    multi: true
  }
];

if (environment.production) {
  enableProdMode();

  Sentry.init(
    {
      dsn: environment.sentryUrl,
      environment: environment.production ? 'production' : 'test',
      release: `ottremit-production-${environment.version.ios}`,
      dist: '1',
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        'localhost',
        'https://crossborder-remittance-test.ott.ca',
        'https://crossborder-remittance.ott.ca',
        'https://app.ottremit.com',
        'https://app-test.ottremit.com'
      ],
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration()
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0
    },
    SentryAngular.init
  );
}

const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        },
        isolate: false
      })
    ),
    ...APP_INITIALIZERS,
    ...(environment.production ? SENTRY : []),
    provideEnvironmentNgxMask(),
    provideRouter(routes, withComponentInputBinding(), withPreloading(PreloadAllModules)),
    provideIonicAngular({ mode: 'ios', innerHTMLTemplatesEnabled: true }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideHttpClient(withInterceptors([appInterceptor, authInterceptor, loadingInterceptor])),
    provideExperimentalZonelessChangeDetection()
  ]
};

bootstrapApplication(AppComponent, appConfig).catch(err => console.log(err));
